import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Funnel } from 'src/api/generated';
import LabelTextInput from 'src/modules/shared/components/atoms/inputs/label-text-input';
import LabelTextArea from 'src/modules/shared/components/atoms/inputs/label-text-area';
import { Text } from 'src/modules/shared/components/atoms/text';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { PiCheck, PiX, PiEye, PiEyeSlash } from 'react-icons/pi';
import { useEffect } from 'react';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { useFunnelSectionsVisibility } from 'src/modules/settings/hooks/funnels/sections-visibility.hooks';

const funnelFormSchema = z.object({
  name: z.string().min(1, 'El nombre es requerido'),
  description: z.string().optional(),
});

export type FunnelFormValues = z.infer<typeof funnelFormSchema>;

interface FunnelFormProps {
  funnel?: Funnel;
  onSubmit: (data: FunnelFormValues) => Promise<void> | void;
  onCancel?: () => Promise<void> | void;
  id?: string;
  editMode?: boolean;
  isLoading?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
}

export const FunnelForm = ({
  funnel,
  onSubmit,
  onCancel,
  id,
  editMode = true,
  isLoading = false,
  onDirtyChange,
}: FunnelFormProps) => {
  const sectionsVisibility = useFunnelSectionsVisibility();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
    reset,
  } = useForm<FunnelFormValues>({
    resolver: zodResolver(funnelFormSchema),
    defaultValues: {
      name: funnel?.name || '',
      description: funnel?.description || '',
    },
  });

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const submitHandler = async (data: FunnelFormValues) => {
    await onSubmit(data);
  };

  const handleCancel = () => {
    reset();
    onCancel?.();
  };

  if (!editMode) {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-4 min-w-0">
          <Text size="h6" weight="semibold" className="leading-tight truncate">
            {funnel?.name}
          </Text>
          {sectionsVisibility && (
            <Tooltip
              // TODO: check when implementing i18n
              content={
                sectionsVisibility.allSectionsClosed
                  ? 'Ver todas las secciones'
                  : 'Ocultar todas las secciones'
              }
              placement="top"
              size="sm"
            >
              <Button
                type="button"
                variant="primary"
                appearance="soft"
                size="sm"
                className="self-center shrink-0 gap-2"
                onClick={() =>
                  sectionsVisibility.allSectionsClosed
                    ? sectionsVisibility.expandAll()
                    : sectionsVisibility.collapseAll()
                }
              >
                {sectionsVisibility.allSectionsClosed ? (
                  <>
                    <PiEye className="h-4 w-4" />
                    <Text size="small">Ver secciones</Text>
                  </>
                ) : (
                  <>
                    <PiEyeSlash className="h-4 w-4" />
                    <Text size="small">Ocultar secciones</Text>
                  </>
                )}
              </Button>
            </Tooltip>
          )}
        </div>
        {funnel?.description && (
          <Text size="small" className="whitespace-pre-wrap">
            {funnel.description}
          </Text>
        )}
      </div>
    );
  }

  return (
    <form
      id={id}
      onSubmit={handleSubmit(submitHandler)}
      className="flex w-full gap-2"
    >
      <div className="flex flex-col gap-4 w-full">
        <LabelTextInput
          label="Nombre"
          inputProps={{
            ...register('name'),
            // TODO: check when implementing i18n
            placeholder: 'Nombra el embudo',
            error: errors.name?.message,
            disabled: isLoading || isSubmitting,
          }}
          labelPosition="top"
        />

        <LabelTextArea
          label="Descripción"
          inputProps={{
            ...register('description'),
            // TODO: check when implementing i18n
            placeholder: 'Explica para qué usarás el embudo',
            error: errors.description?.message,
            rows: 4,
            resize: 'vertical',
            disabled: isLoading || isSubmitting,
          }}
          labelPosition="top"
        />
      </div>

      <div className="flex items-start gap-2 justify-end">
        <Button
          type="submit"
          variant="primary"
          appearance="filled"
          size="sm"
          isIconOnly
          loading={isLoading || isSubmitting}
          disabled={!isDirty || isLoading || isSubmitting}
        >
          <PiCheck className="h-5 w-5" />
        </Button>

        {onCancel && (
          <Button
            type="button"
            variant="neutral"
            appearance="soft"
            size="sm"
            isIconOnly
            disabled={isLoading || isSubmitting}
            onClick={handleCancel}
          >
            <PiX className="h-5 w-5" />
          </Button>
        )}
      </div>
    </form>
  );
};
